import { usePermission } from '@/hooks/usePermission'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api'
import { X, TrendingUp } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { settingsPath } from '@/lib/settingsRoutes'

/**
 * AC-36 — MIDNIGHT in-app soft-cap banner.
 *
 * Pastel butter tile at ≥75%; pastel blush tile at ≥100%.
 * Dismiss X top-right. Dismissal persists per session bucket.
 * API: GET /api/billing/usage
 * Nav: → Settings → Usage (there is no plan billing page)
 */

interface UsageResponse {
  percentOfCap: number
  estimatedCostCents: number
  softCapCents: number
}

function bucketFor(pct: number): 'ok' | '75' | '100' {
  if (pct >= 100) return '100'
  if (pct >= 75)  return '75'
  return 'ok'
}

function dismissKey(bucket: '75' | '100'): string {
  return `usage-cap-banner-dismissed-${bucket}`
}

export function UsageCapBanner(): React.ReactElement | null {
  const navigate       = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  // Only roles that can see the spend meter ask for it (Crew got a 403 on
  // every page).
  const canSeeUsage = usePermission('viewUsage')
  const [usage,     setUsage]    = useState<UsageResponse | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!isAuthenticated || !canSeeUsage) return
    let cancelled = false
    apiClient
      .get('/api/billing/usage')
      .then((res) => {
        if (cancelled) return
        const data = (res.data?.data ?? res.data) as UsageResponse
        setUsage(data)
        const bucket = bucketFor(data.percentOfCap)
        if (bucket !== 'ok') {
          setDismissed(sessionStorage.getItem(dismissKey(bucket as '75' | '100')) === '1')
        }
      })
      .catch(() => { /* silent */ })
    return () => { cancelled = true }
  }, [isAuthenticated, canSeeUsage])

  if (!usage) return null
  const bucket = bucketFor(usage.percentOfCap)
  if (bucket === 'ok') return null
  if (dismissed) return null

  const isOver = bucket === '100'
  const overageCents  = isOver ? Math.max(0, usage.estimatedCostCents - usage.softCapCents) : 0
  const overageDollars = (overageCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

  // Phase rule (lib/phaseTone): over the cap is blush; the 75% heads-up is
  // neutral glass with white text (it wants attention, it isn't a phase).
  const tileBg     = isOver ? 'rgb(var(--blush-rgb) / 0.12)' : 'rgb(var(--fg-rgb) / 0.06)'
  const tileBorder = isOver ? 'rgb(var(--blush-rgb) / 0.28)' : 'rgb(var(--fg-rgb) / 0.12)'
  const textColor  = isOver ? 'var(--blush)' : 'rgb(var(--fg-rgb))'
  const dotColor   = textColor

  function handleDismiss() {
    sessionStorage.setItem(dismissKey(bucket as '75' | '100'), '1')
    setDismissed(true)
  }

  return (
    <div
      role="status"
      className="mx-4 mt-3 animate-fade-up"
      style={{
        background: tileBg,
        border: `0.5px solid ${tileBorder}`,
        borderRadius: 18,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Status dot */}
      <span aria-hidden="true" style={{ fontSize: 12, color: dotColor, flexShrink: 0 }}>●</span>

      {/* Message */}
      <div style={{ flex: 1, minWidth: 0, fontSize: 14, color: textColor, letterSpacing: '-0.01em', lineHeight: 1.4 }}>
        {isOver ? (
          <span>
            <span style={{ fontWeight: 600 }}>Monthly soft cap reached.</span>
            {' '}Calls and texts keep working —{' '}
            {overageCents > 0 ? (
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{overageDollars} over included</span>
            ) : (
              'additional usage will be billed'
            )}
            .
          </span>
        ) : (
          <span>
            You're at{' '}
            <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{usage.percentOfCap}%</span>
            {' '}of your monthly usage cap (calls, texts and AI).
          </span>
        )}
      </div>

      {/* CTA row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Upgrade pill */}
        <button
          type="button"
          onClick={() => navigate(settingsPath('usage'))}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 12px',
            borderRadius: 9999,
            fontSize: 13, fontWeight: 600,
            background: 'var(--accent)',
            border: '0.5px solid transparent',
            color: 'var(--accent-ink)',
            cursor: 'pointer',
            transition: 'background 200ms',
            touchAction: 'manipulation',
          }}
        >
          <TrendingUp style={{ width: 12, height: 12 }} aria-hidden />
          Review usage
        </button>

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss usage cap banner"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 9999, padding: 0,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--midnight-text3)',
            transition: 'background 150ms',
            touchAction: 'manipulation',
          }}
          onMouseEnter={e => { (e.currentTarget).style.background = 'rgb(var(--fg-rgb) / 0.08)' }}
          onMouseLeave={e => { (e.currentTarget).style.background = 'transparent' }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
    </div>
  )
}

export default UsageCapBanner
